import { useNavigate, useSearchParams } from "react-router";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FoldersSidebar } from "@/components/sidebar/sidebar";
import { useWindowSize } from "usehooks-ts";
import { useLocation } from "react-router";
import { useAuth } from "@clerk/react-router";
import { useLocalStorage } from "@/components/hooks/use-local-storage";
import { cn } from "@/lib/utils";
import { useSidebarCollapse } from "./sidebar/context";

// Create a context that will hold the ref
const PanelContext =
  createContext<React.RefObject<HTMLDivElement | null> | null>(null);

export const usePanelRef = () => useContext(PanelContext);

export const SidebarWrapper = ({ children }: { children: React.ReactNode }) => {
  const { width } = useWindowSize();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { userId } = useAuth();
  const [isSidebarHover, setIsSidebarHover] = useState(false);

  const { isCollapsed, setIsCollapsed, openStatus, setOpenStatus } =
    useSidebarCollapse();
  const [userInfo] = useLocalStorage<Record<string, any>>("user-info", {});
  const isMobile = width < 768;

  useEffect(() => {
    const viewType = params.get("view") as string | null;

    if (pathname.startsWith("/dashboard")) {
      const folderId = params.get("folder") as string | null;
      if (!folderId) {
        setIsCollapsed(false);
      }
    }

    if (
      pathname.startsWith("/memo-ai") ||
      pathname.startsWith("/quiz/") ||
      pathname.startsWith("/add") ||
      pathname.startsWith("/upgrade")
    ) {
      setIsCollapsed(true);
    }

    if (viewType === "pdf" || viewType === "flashcards") {
      setIsCollapsed(true);
    }
  }, [pathname, params]);

  const panelRef = useRef<HTMLDivElement>(null);

  const isNewLandingPage = pathname === "/" || pathname.startsWith("/home");
  const isHidden =
    ["/home", "/", "/updates", "/settings"].includes(pathname) ||
    isNewLandingPage ||
    isMobile ||
    ((pathname === "/dashboard" || pathname === "/welcome") &&
      (userId ? userInfo[userId] : undefined) == undefined);

  const DURATION_TRANSITION = "duration-200";

  const isSidebarHoverState = openStatus === "hover" || isSidebarHover;

  return (
    <>
      <div className="flex flex-row h-full w-full">
        {!isHidden && (
          <>
            {isSidebarHoverState ? (
              <div
                className="fixed left-2 top-24 h-[20px] w-64 z-[200]"
                onMouseEnter={() => setIsSidebarHover(true)}
                onMouseLeave={() => setIsSidebarHover(false)}
              ></div>
            ) : (
              <></>
            )}
            <div
              className={cn(
                DURATION_TRANSITION,
                "h-[calc(100dvh-48px)] top-12 fixed flex flex-col gap-1 py-3 justify-between min-w-[50px] transition-all border-r-2 border-secondary-100 z-[199] bg-secondary-50",
                !isCollapsed
                  ? ""
                  : isSidebarHoverState
                  ? "fixed top-28 h-[calc(100vh-120px)] border-2 border-secondary-100 left-2 rounded-xl shadow-lg"
                  : "-translate-x-full top-28"
              )}
              onMouseEnter={() => setIsSidebarHover(true)}
              onMouseLeave={() => setIsSidebarHover(false)}
            >
              <FoldersSidebar
                className="p-4"
                onClickFolder={(folderId) => {
                  navigate(`/dashboard?folder=${folderId}`);
                }}
              />
            </div>
          </>
        )}
        {/* // This is the hover effect for the left-most side of the screen */}
        {(isCollapsed && openStatus === "closed") || openStatus === "hover" ? (
          <div
            className="fixed h-[calc(100vh)] left-0 w-2"
            onMouseEnter={() => setOpenStatus("hover")}
            onMouseLeave={() => setOpenStatus("closed")}
          ></div>
        ) : (
          <></>
        )}
        {/* // Rendering the content, with padding left equal to the sidebar presence*/}
        <div
          className={cn(
            DURATION_TRANSITION,
            "w-full flex flex-col items-center transition-all",
            isNewLandingPage ? "mt-0" : "mt-12",
            !isHidden ? (isCollapsed ? "" : "pl-64") : ""
          )}
          ref={panelRef}
        >
          <PanelContext.Provider value={panelRef}>
            {children}
          </PanelContext.Provider>
        </div>
      </div>
    </>
  );
};
